const toNum = (value, fallback) => {
  const n = typeof value === "string" ? parseFloat(value) : value;
  return typeof n === "number" && !Number.isNaN(n) ? n : fallback;
};

// Blob URLs created by URL.createObjectURL() are session-local and expire on page reload.
// If the backend persisted a blob URL (data corruption), strip it so the editor doesn't
// try to render an image that no longer exists in this session's object URL registry.
const sanitizeUrl = (url) =>
  typeof url === "string" && url.startsWith("blob:") ? null : url;

// Backends (Laravel especially) often return numeric DB columns as strings.
// Mixing a string position with a numeric mouse delta via `+` silently does
// string concatenation instead of addition (e.g. "150" + 5 = "1505"), which
// flings dragged elements off-canvas. Coerce every numeric field to a real
// JS number once, right when animation data enters the app.
export function normalizeAnimationData(data) {
  if (!data) return data;

  const elements = (data.elements || []).map((el) => {
    if (!el.state) return el;
    const normalized = {
      ...el,
      // Strip expired blob URLs — they can't be loaded after a page reload
      ...(el.src !== undefined ? { src: sanitizeUrl(el.src) } : {}),
      state: {
        ...el.state,
        position: {
          x: toNum(el.state.position?.x, 0),
          y: toNum(el.state.position?.y, 0),
        },
        size: {
          w: toNum(el.state.size?.w, 100),
          h: toNum(el.state.size?.h, 100),
        },
        rotation: toNum(el.state.rotation, 0),
        opacity: toNum(el.state.opacity, 1),
        scale: toNum(el.state.scale, 1),
      },
    };
    if (el.type === "text") {
      normalized.fontSize = toNum(el.fontSize, 28);
    }
    return normalized;
  });

  const interactions = (data.interactions || []).filter(Boolean).map((int) => ({
    ...int,
    actions: (int.actions || []).map((act) => ({
      ...act,
      to: act.to
        ? Object.fromEntries(Object.entries(act.to).map(([k, v]) => [k, toNum(v, 0)]))
        : act.to,
      duration: toNum(act.duration, 1),
      delay: toNum(act.delay, 0),
    })),
  }));

  return {
    ...data,
    elements,
    interactions,
    totalDuration: toNum(data.totalDuration, 3),
    canvasBackground: sanitizeUrl(data.canvasBackground),
  };
}

// Converts the editor's session-local animation data (which keeps blob URLs and
// assetKey/canvasBackgroundKey around so the editor can re-render and re-open cleanly)
// into the clean payload shape the backend expects: src/canvasBackground hold either
// a real persisted URL or a temp upload placeholder key, never a blob URL.
export function resolveAnimationDataForUpload(data) {
  if (!data) return data;

  const elements = (data.elements || []).map((el) => {
    if (el.type !== "image") return el;
    const { assetKey, ...rest } = el;
    return { ...rest, src: assetKey || el.src };
  });

  const { canvasBackgroundKey, ...rest } = data;

  return {
    ...rest,
    elements,
    canvasBackground: canvasBackgroundKey || data.canvasBackground,
  };
}
